import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { InquiryServiceType } from '@webapp/core/enums';
import {
  ADDITIONAL_ROOM_OPTIONS,
  BATHROOM_COUNT_OPTIONS,
  BLINDS_OPTIONS,
  FLOOR_COVERING_OPTIONS,
} from '@webapp/core/constants/inquiry-labels';
import { CreateInquiryStep3_5 } from '@webapp/core/interfaces';
import { ToastrService } from 'ngx-toastr';
import {
  InquiryDraftSource,
  PublicInquiryManagerService,
} from 'projects/webapp/app/services/public-inquiry-manager.service';


/** Reinigung: what the cleaning covers. */
@Component({
  selector: 'app-inquiry-step3-5',
  templateUrl: './step3.5.component.html',
})
export class InquiryStep35Component implements OnInit, OnDestroy {
  public draftInquiryForm = new FormGroup({
    floor_coverings: new FormControl<Set<string>>(new Set(), { nonNullable: true }),
    blinds: new FormControl('', { nonNullable: true }),
    additional_rooms: new FormControl<Set<string>>(new Set(), { nonNullable: true }),
    nur_wc: new FormControl('0', { nonNullable: true }),
    dusche_ws: new FormControl('0', { nonNullable: true }),
    bad_wc: new FormControl('0', { nonNullable: true }),
    dusche_bad_wc: new FormControl('0', { nonNullable: true }),
    comment: new FormControl('', { nonNullable: true }),
  });

  /**
   * A cleaning-only inquiry has no Services step, so the comment is asked
   * here. With a move, the Services step already took it.
   */
  hasComment = false;

  bathroomOptions = BATHROOM_COUNT_OPTIONS;

  floorCoveringsOptions = FLOOR_COVERING_OPTIONS;

  blindsOptions = BLINDS_OPTIONS;

  additionalRoomsOptions = ADDITIONAL_ROOM_OPTIONS;

  protected _draftSource: InquiryDraftSource = () => ({ step3_5: this._buildPayload() });

  constructor(
    protected _toastrService: ToastrService,
    protected _publicInquiryManagerService: PublicInquiryManagerService,
  ) {
  }

  ngOnInit() {
    this.hasComment = this._publicInquiryManagerService.inquiryCreationProgress?.service === InquiryServiceType.CLEAN;
    this._loadFormData();
    this._publicInquiryManagerService.setDraftSource(this._draftSource);
  }

  ngOnDestroy() {
    this._publicInquiryManagerService.clearDraftSource(this._draftSource);
  }

  onSubmit() {
    this.draftInquiryForm.markAllAsTouched();
    if (!this.draftInquiryForm.valid) {
      this._toastrService.warning('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    this._publicInquiryManagerService.submitStep3_5(this._buildPayload())
  }

  goBack() {
    this._publicInquiryManagerService.goPrevStep()
  }

  isFloorCoveringsActive(value: string) {
    return this.draftInquiryForm.controls.floor_coverings.value.has(value);
  }

  toggleFloorCoverings(value: string) {
    const control = this.draftInquiryForm.controls.floor_coverings;
    if (control.value.has(value)) control.value.delete(value);
    else control.value.add(value);
  }

  isBlindsActive(value: string) {
    return this.draftInquiryForm.controls.blinds.value === value;
  }

  selectBlinds(value: string) {
    this.draftInquiryForm.controls.blinds.setValue(value);
  }

  isAdditionalRoomsActive(value: string) {
    return this.draftInquiryForm.controls.additional_rooms.value.has(value);
  }

  toggleAdditionalRooms(value: string) {
    const control = this.draftInquiryForm.controls.additional_rooms;
    if (control.value.has(value)) control.value.delete(value);
    else control.value.add(value);
  }

  protected _loadFormData() {
    const step3_5Data = this._publicInquiryManagerService.inquiryCreationProgress?.step3_5;
    if (!step3_5Data) return;

    this.draftInquiryForm.patchValue({
      ...step3_5Data,
      floor_coverings: new Set(step3_5Data.floor_coverings),
      additional_rooms: new Set(step3_5Data.additional_rooms),
    });
  }

  protected _buildPayload(): CreateInquiryStep3_5 {
    const controls = this.draftInquiryForm.controls;
    return {
      floor_coverings: [...controls.floor_coverings.value],
      blinds: controls.blinds.value,
      additional_rooms: [...controls.additional_rooms.value],
      nur_wc: controls.nur_wc.value,
      dusche_ws: controls.dusche_ws.value,
      bad_wc: controls.bad_wc.value,
      dusche_bad_wc: controls.dusche_bad_wc.value,
      ...(this.hasComment ? { comment: controls.comment.value } : {}),
    };
  }
}
